import { useQueryCache } from '@pinia/colada'
import { HANDWRITTEN_CUSTOMIZE_LOGIN_INTENT_KEY } from '~~/layers/base/constants/handwrittenCustomizeLoginIntent'
import { clearProfileInProgressHandwritingSilent } from '~~/layers/base/composables/useHandwritingInProgressPointer'
import { useContactStore } from '~~/layers/base/stores/contact.store'
import { useDesignProjectStore } from '~~/layers/base/stores/designProject.store'
import { useHandwritingCatalogStore } from '~~/layers/base/stores/handwritingCatalog.store'
import { useHandwritingProjectStore } from '~~/layers/base/stores/handwritingProject.store'
import { useHandwrittenNoteFormStore } from '~~/layers/base/stores/handwrittenNoteForm.store'
import { usePersonalizeFormStore } from '~~/layers/base/stores/personalizeForm.store'
import { useProfileStore } from '~~/layers/base/stores/profile.store'
import { useReturnAddressStore } from '~~/layers/base/stores/returnAddress.store'
import { useShoppingSessionStore } from '~~/layers/base/stores/shoppingSession.store'
import { clearCheckoutEntryPath } from '~~/layers/base/utils/checkoutLoginGate'
import { clearHandwritingNoteSuccessRecipient } from '~~/layers/base/utils/handwritingNoteSuccessRecipient'

/** Account-scoped Colada keys that can hold prior-account PII (Capacitor persists the cache). */
const ACCOUNT_SCOPED_COLADA_KEY_PREFIXES = [['recentContacts'], ['recentMessages']] as const

export type ClearClientSessionOnLogoutOptions = {
    /**
     * Skip wiping `useProfileStore` (name / addresses). Use while still on Account so the
     * signed-in profile UI stays intact until navigation (TK-13098); Home Colada/session
     * caches are still cleared so `/` mounts fresh under the screen lock.
     */
    preserveProfileStore?: boolean
}

/**
 * Wipes client Send Handwritten Note / checkout / designer session artifacts after Better Auth
 * sign-out (TK-12970). Does not touch guest Magento quote cookies or server DB rows.
 *
 * Call from another composable / setup so Pinia stores are resolved in a Nuxt context.
 * Prefer {@link pauseAutosaveBeforeAnonymousRestore} *before* `signIn.anonymous()` so a
 * debounced PATCH cannot land under the new guest (and later merge into whoever signs in).
 * Safe to call repeatedly.
 */
export function useClearClientSessionOnLogout() {
    const shoppingSessionStore = useShoppingSessionStore()
    const handwrittenFormStore = useHandwrittenNoteFormStore()
    const handwritingProjectStore = useHandwritingProjectStore()
    const returnAddressStore = useReturnAddressStore()
    const contactStore = useContactStore()
    const handwritingCatalogStore = useHandwritingCatalogStore()
    const profileStore = useProfileStore()
    const personalizeFormStore = usePersonalizeFormStore()
    const designProjectStore = useDesignProjectStore()
    const queryCache = useQueryCache()

    /**
     * Stop note/designer autosave before anonymous restore. Pending timers can still fire after
     * `signOut` if only the flag flips; cancel closes that window.
     */
    function pauseAutosaveBeforeAnonymousRestore(): void {
        if (!import.meta.client) {
            return
        }
        handwrittenFormStore.setAutosaveEnabled(false)
        handwrittenFormStore.cancelHandwritingAutosaveDebounces()
        personalizeFormStore.cancelTextDebounce()
        personalizeFormStore.cancelNonTextDebounce()
    }

    function removeAccountScopedColadaCache(): void {
        for (const key of ACCOUNT_SCOPED_COLADA_KEY_PREFIXES) {
            queryCache.cancelQueries({ key: [...key] })
            for (const entry of queryCache.getEntries({ key: [...key] })) {
                queryCache.remove(entry)
            }
        }
    }

    async function clearClientSessionOnLogout(
        options: ClearClientSessionOnLogoutOptions = {}
    ): Promise<void> {
        if (!import.meta.client) {
            return
        }

        handwrittenFormStore.setAutosaveEnabled(false)
        handwrittenFormStore.resetHandwrittenNoteFormState()
        shoppingSessionStore.resetCheckout()
        handwritingProjectStore.clearHandwritingProject()
        returnAddressStore.clearAddresses()
        contactStore.clearSessionContacts()
        handwritingCatalogStore.setSendFlowContext(false)
        // Before/independent of the context flip: the cached verdict is keyed by account, so it must
        // be dropped while this session key still resolves.
        await handwritingCatalogStore.clearHasCustomStationery()
        if (!options.preserveProfileStore) {
            profileStore.clearAddresses()
        }
        // Clear design project first — `resetPersonalizeFormState` re-inits from elements if any remain.
        designProjectStore.clearDesignProject()
        personalizeFormStore.resetPersonalizeFormState()
        removeAccountScopedColadaCache()

        clearCheckoutEntryPath()
        clearHandwritingNoteSuccessRecipient()
        await clearProfileInProgressHandwritingSilent()

        try {
            sessionStorage.removeItem(HANDWRITTEN_CUSTOMIZE_LOGIN_INTENT_KEY)
        } catch {
            /* private mode / unavailable */
        }

        try {
            localStorage.removeItem('e_interrupted_page')
        } catch {
            /* private mode / unavailable */
        }
    }

    return {
        pauseAutosaveBeforeAnonymousRestore,
        clearClientSessionOnLogout,
    }
}
